import type {SafeHTMLString} from '@github-ui/safe-html'
import {useSearchParams} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useSafeTimeout} from '@primer/react'
import {useRef, useState} from 'react'

export interface ListResults<TRepo> {
  repositories: TRepo[]
  repositoryCount: number
  pageCount: number
}

export type PrimaryLanguage = {name: string; color: string}
export type LastUpdated = {hasBeenPushedTo: boolean; timestamp: string}

export interface RepositoryItem {
  type: string
  name: string
  owner: string
  isFork: boolean
  description: SafeHTMLString
  topicNames: string[]
  topicsNotShown: number
  primaryLanguage: PrimaryLanguage | null
  pullRequestCount: number | null
  issueCount: number | null
  starsCount: number
  forksCount: number
  license: string | null
  lastUpdated: LastUpdated
  participation?: number[]
}

interface HookType<TRepo> {
  results: ListResults<TRepo>
  currentPage: number
  isFetching: boolean
  fetchResults(params: {q: string; page: number}): void
}

export function useListResults<TRepo>(
  initialResults: ListResults<TRepo>,
  fetchUrl: string,
  initialPage: number | undefined,
): HookType<TRepo> {
  const [results, setResults] = useState(initialResults)
  const [currentPage, setCurrentPage] = useState(initialPage || 1)
  const [isFetching, setFetching] = useState(false)
  const latestUrlRef = useRef('')
  const [pageParams] = useSearchParams()
  const {safeSetTimeout} = useSafeTimeout()

  async function sleep(ms: number) {
    return new Promise(resolve => safeSetTimeout(resolve, ms))
  }

  async function fetchResults({q, page}: {q: string; page: number}) {
    try {
      const fetchParams = new URLSearchParams({q, page: page.toString()})
      if (pageParams.has('type')) {
        fetchParams.set('type', pageParams.get('type')!)
      }
      const url = `${fetchUrl}?${fetchParams}`
      latestUrlRef.current = url

      await sleep(400)
      setFetching(true)
      if (latestUrlRef.current !== url) return

      const response = await verifiedFetchJSON(url)
      if (latestUrlRef.current !== url) return
      if (!response.ok) return

      const newResults = (await response.json()) as ListResults<TRepo>
      setResults(newResults)
      setCurrentPage(page)
      setFetching(false)

      if (q) {
        pageParams.set('q', q)
      } else {
        pageParams.delete('q')
      }
      if (page > 1) {
        pageParams.set('page', page.toString())
      } else {
        pageParams.delete('page')
      }
      history.replaceState(undefined, '', `?${pageParams}`)
    } catch (e) {
      // Handle network errors silently, keep previous results
      setFetching(false)
    }
  }

  return {results, currentPage, isFetching, fetchResults}
}
